/**
 * Update mutation for useMemoRecords — diffs a note/task edit (properties,
 * mentions, owner, stage) against its original values and submits only the
 * changed pieces. Split out of useMemoRecords.ts — see that file for the
 * composed public hook.
 */

import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { submitEventsOptimistic } from '../../store/submitEventsOptimistic';
import { extractMentionedUserIds } from './mentionUtils';
import type { MemoAssociationNames, MemoEntityType } from './notes.utils';
import { dueProperties, validateMentionedUserIds } from './notes.utils';

export interface UseUpdateRecordMutationParams {
  orgId: number;
  recordType: MemoEntityType;
  names: MemoAssociationNames;
  notesListKey: QueryKey;
  ownerAssociationTypeName: string | null;
  validUserEntityIds: Set<string>;
  invalidateNoteObjectQuery: (noteEntityId: string) => void;
}

/** The update mutation, sharing the association/query context resolved by useMemoRecordsQueries. */
export function useUpdateRecordMutation({
  orgId,
  recordType,
  names,
  notesListKey,
  ownerAssociationTypeName,
  validUserEntityIds,
  invalidateNoteObjectQuery,
}: UseUpdateRecordMutationParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteEntityId,
      title,
      content,
      date,
      ownerEntityId,
      originalTitle,
      originalContent,
      originalDate,
      originalDatetime,
      originalOwnerEntityId,
      lastEventSeq,
      userId,
      stageAssociation,
      originalStageAssociation,
      canAddMentions = true,
      canRemoveMentions = true,
    }: {
      noteEntityId: string;
      title: string;
      content: string;
      date?: string;
      /** New owner entity_id picked via the avatar popover. Optional. */
      ownerEntityId?: string;
      originalTitle: string;
      originalContent: string;
      originalDate?: string;
      /** Note's ahoy_datetime at edit start (ISO). Takes precedence over
       * originalDate when diffing the due value. */
      originalDatetime?: string;
      /** Owner at edit start, used to detect a change and re-associate. */
      originalOwnerEntityId?: string | null;
      lastEventSeq: number;
      userId: string;
      /** Newly-selected stage entity_id; null means "un-task". */
      stageAssociation?: string | null;
      /** Stage entity_id at edit start, used to detect change + emit remove. */
      originalStageAssociation?: string | null;
      /**
       * Whether the caller may ADD mention associations (gated on
       * `ahoy_association_added`). Defaults to true. When false, newly-added
       * mentions are not emitted so a content-only edit isn't rejected for an
       * association write the user can't make.
       */
      canAddMentions?: boolean;
      /**
       * Whether the caller may REMOVE mention associations (gated on
       * `ahoy_association_removed`). Defaults to true. Gated SEPARATELY from
       * adds: a user with add-but-not-remove permission would otherwise emit an
       * `ahoy_association_removed` on mention deletion that the backend rejects,
       * failing the entire (otherwise-authorized) description/text save.
       */
      canRemoveMentions?: boolean;
    }) => {
      // Only include properties that changed
      const changedProperties: Record<string, string | null> = {};
      if (title !== originalTitle) {
        changedProperties.ahoy_title = title;
      }
      if (content !== originalContent) {
        changedProperties.ahoy_content = content;
      }
      // Due value: tasks only — notes have no date fields (#13884, backend
      // returns 400 for ahoy_date/ahoy_datetime on ahoy_note).
      // `date` carries a YYYY-MM-DD date or a full ISO datetime (when a time
      // is set). Diff against the original due (datetime takes precedence over
      // date). When it changed, write the pair per dueProperties: clears null
      // both, date-only sets the date and clears the stale datetime, and a
      // datetime writes ONLY ahoy_datetime — the backend NoteDueDateCascade
      // derives ahoy_date server-side (the key is absent from the returned
      // pair, so it is not written at all). Empty strings normalize to
      // undefined so '' (cleared) and undefined (never set) are treated
      // identically.
      if (recordType !== 'ahoy_note') {
        const newDue = date || undefined;
        const originalDue = originalDatetime || originalDate || undefined;
        if (newDue !== originalDue) {
          const due = dueProperties(newDue);
          if (due.ahoy_date !== undefined) {
            changedProperties.ahoy_date = due.ahoy_date;
          }
          changedProperties.ahoy_datetime = due.ahoy_datetime;
        }
      }

      // Extract and validate mentioned users from both original and new
      // content, then emit each side of the diff only when the caller holds the
      // matching association permission. Adds and removes are gated
      // INDEPENDENTLY so a user who may add but not remove (or vice versa) still
      // gets their authorized half applied and their content-only save
      // succeeds, instead of the whole batch being rejected for an event they
      // can't make. When the caller can do neither, the diff is skipped
      // entirely (both flags false).
      let addedMentions: string[] = [];
      let removedMentions: string[] = [];
      if (canAddMentions || canRemoveMentions) {
        const originalMentionedIds = extractMentionedUserIds(originalContent);
        const newMentionedIds = extractMentionedUserIds(content);
        const validOriginalMentionedIds = new Set(
          validateMentionedUserIds(originalMentionedIds, validUserEntityIds)
        );
        const validNewMentionedIds = new Set(
          validateMentionedUserIds(newMentionedIds, validUserEntityIds)
        );

        // Compute diff: added = new - original, removed = original - new
        if (canAddMentions) {
          addedMentions = [...validNewMentionedIds].filter(
            id => !validOriginalMentionedIds.has(id)
          );
        }
        if (canRemoveMentions) {
          removedMentions = [...validOriginalMentionedIds].filter(
            id => !validNewMentionedIds.has(id)
          );
        }
      }

      // Owner change: re-associate via remove + add. Skip when the
      // owner association type isn't known yet, or when ownerEntityId
      // hasn't actually changed.
      const normalizedNewOwner = ownerEntityId || undefined;
      const normalizedOrigOwner = originalOwnerEntityId || undefined;
      const ownerChanged =
        ownerAssociationTypeName !== null &&
        normalizedNewOwner !== normalizedOrigOwner;

      const normalizedNewStage = stageAssociation || undefined;
      const normalizedOrigStage = originalStageAssociation || undefined;
      // names.stage is null for the note family (no stage concept) — a stage
      // diff can only ever be written for a record type that has a real
      // stage association name.
      const stageChanged =
        names.stage !== null && normalizedNewStage !== normalizedOrigStage;

      // If nothing changed (no property changes, no mention changes,
      // no owner change, no stage change), skip the update entirely.
      const hasPropertyChanges = Object.keys(changedProperties).length > 0;
      const hasMentionChanges =
        addedMentions.length > 0 || removedMentions.length > 0;
      if (
        !hasPropertyChanges &&
        !hasMentionChanges &&
        !ownerChanged &&
        !stageChanged
      ) {
        return;
      }

      // many_to_one cardinality: the wire batch stays add-only (the backend
      // cascade replaces the prior stage server-side) — fold the implicit
      // removal of the prior target into THIS batch's optimistic rollback
      // context via singleCardinalityReplacements, so both rollback paths
      // (snapshot-restore and the surgical revertAssociations path) reverse
      // it. See applySingleCardinalityReplacements for details.
      const singleCardinalityReplacements =
        stageChanged && names.stage && normalizedNewStage && normalizedOrigStage
          ? [
              {
                entityId: noteEntityId,
                associationTypeName: names.stage,
                priorTargetEntityId: normalizedOrigStage,
                newTargetEntityId: normalizedNewStage,
              },
            ]
          : undefined;

      return submitEventsOptimistic({
        orgId,
        userId,
        entityTypeName: recordType,
        primaryEntityId: noteEntityId,
        fallbackSeq: lastEventSeq,
        singleCardinalityReplacements,
        build: builder => {
          if (hasPropertyChanges) {
            builder.propertiesUpdated(
              noteEntityId,
              recordType,
              changedProperties
            );
          }

          // Add associations for newly mentioned users
          for (const mentionedUserId of addedMentions) {
            builder.associationAdded(
              noteEntityId,
              recordType,
              names.mentionedUser,
              mentionedUserId
            );
          }

          // Remove associations for users no longer mentioned
          for (const mentionedUserId of removedMentions) {
            builder.associationRemoved(
              noteEntityId,
              recordType,
              names.mentionedUser,
              mentionedUserId
            );
          }

          // Owner re-association: drop the previous owner link, add the new
          // one. Both events emit so the projection reflects the swap.
          if (ownerChanged && ownerAssociationTypeName) {
            if (normalizedOrigOwner) {
              builder.associationRemoved(
                noteEntityId,
                recordType,
                ownerAssociationTypeName,
                normalizedOrigOwner
              );
            }
            if (normalizedNewOwner) {
              builder.associationAdded(
                noteEntityId,
                recordType,
                ownerAssociationTypeName,
                normalizedNewOwner
              );
            }
          }

          // many_to_one cardinality: the wire batch stays add-only (the
          // backend cascade replaces the prior stage server-side); the
          // implicit removal of the prior target is folded into this
          // batch's rollback context via singleCardinalityReplacements
          // above.
          if (stageChanged && names.stage) {
            if (normalizedNewStage) {
              builder.associationAdded(
                noteEntityId,
                recordType,
                names.stage,
                normalizedNewStage
              );
            } else if (normalizedOrigStage) {
              builder.associationRemoved(
                noteEntityId,
                recordType,
                names.stage,
                normalizedOrigStage
              );
            }
          }
        },
        errorToast: false,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: notesListKey });
      invalidateNoteObjectQuery(variables.noteEntityId);
    },
    onError: error => {
      console.error('Failed to update note with mentions:', error);
    },
  });
}
